// Animated northern lights backdrop; replaces the old GNSS wave animation.
import React, { useEffect, useRef } from "react";

export default function GnssWaveEffect() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = 0;
    let raf;
    let width = 0;
    let height = 0;
    let stars = [];

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(100, Math.floor(width * height / 14000));
      stars = Array.from({ length: count }, (_, i) => ({
        x: ((i * 0.61803398875) % 1) * width,
        y: ((i * 0.38196601125 + 0.13) % 1) * height * 0.72,
        r: 0.45 + ((i * 11) % 8) / 10,
        a: 0.12 + ((i * 7) % 10) / 28,
      }));
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);
      const bg = ctx.createLinearGradient(0, 0, 0, height);
      bg.addColorStop(0, "#030b19");
      bg.addColorStop(0.58, "#071829");
      bg.addColorStop(1, "#07121e");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      stars.forEach((star) => {
        ctx.globalAlpha = star.a * (0.9 + 0.1 * Math.sin(frame * 0.01 + star.x));
        ctx.fillStyle = "#d8f9ff";
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      // Translucent ribbons: teal, arctic green, and violet.
      [
        { baseline: 0.12, hue: "91, 255, 187", alpha: 0.19, shift: 0 },
        { baseline: 0.18, hue: "49, 227, 220", alpha: 0.16, shift: 1.8 },
        { baseline: 0.1, hue: "144, 113, 245", alpha: 0.10, shift: 3.4 },
      ].forEach((ribbon) => {
        const phase = frame * 0.002 + ribbon.shift;
        for (let layer = 0; layer < 13; layer++) {
          ctx.beginPath();
          const yOffset = layer * 13;
          for (let x = -15; x <= width + 15; x += 9) {
            const y = height * ribbon.baseline + yOffset +
              Math.sin(x * 0.004 + phase) * (38 + layer * 2.3) +
              Math.sin(x * 0.009 - phase * 0.8) * 28;
            if (x === -15) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.lineWidth = 10 + layer * 1.5;
          ctx.strokeStyle = `rgba(${ribbon.hue}, ${ribbon.alpha * (1 - layer / 14)})`;
          ctx.shadowBlur = 34;
          ctx.shadowColor = `rgba(${ribbon.hue}, 0.35)`;
          ctx.stroke();
        }
      });
      ctx.shadowBlur = 0;
      if (!reducedMotion) {
        frame++;
        raf = requestAnimationFrame(draw);
      }
    }

    resize();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="aurora-background" aria-hidden="true" />;
}
